// ScenarioIQ Context for State Management

import React, { createContext, useContext, ReactNode } from 'react';
import { useMsal } from '@azure/msal-react';
import {
  Scenario,
  ScenarioResult,
  ScenarioSummary,
  UserProductFamilies,
  LoadingState,
  ScenarioFormState,
  ProductFamilySelection,
  ExcludedOpportunity,
  PayerProfitabilityItem,
  FeeScheduleGap,
  RebateDetail
} from '../types';

/** Key for per-scenario data: generated scenario ids restart at 1 in every therapeutic area. */
export const scenarioKey = (category: string | null | undefined, scenarioId: number | string) =>
  `${category ?? ''}:${Number(scenarioId)}`;

// User-run alternates are numbered from here, clear of generated ids (≤ 50 per area), so an
// alternate's id is unique across every area of the run.
export const ALTERNATE_SCENARIO_ID_BASE = 1000;

// State interface
interface ScenarioState {
  // Current user and selection
  currentUser: string | null;
  currentCategory: string | null;
  userProductFamilies: UserProductFamilies | null;
  selectedExcludedOpportunities: ExcludedOpportunity[] | null;
  
  // Generator form cache
  generatorState: {
    form: Partial<ScenarioFormState>;
    selectedFamilies: Record<string, ProductFamilySelection>;
    userName: string | null;
    useCurrentOnly: boolean;
    // Last-used generation algorithm, remembered across sessions. Absent = first
    // login → defaults to distribution-based.
    preferredGenerationMode?: 'payer' | 'distribution';
    // The approach and time of the run now on Results, stamped at Generate. Drives the
    // strategy badge and the "generated {date}" subtitle there.
    lastRun?: { mode: 'payer' | 'distribution'; generatedAt: string };
  };

  // Scenarios
  scenarios: Scenario[];
  scenarioResults: ScenarioResult[];
  scenarioSummary: ScenarioSummary[];
  // Background incremental generation status
  generationStatus?: {
    /** Identifies the run; updates from any other (stale) run are dropped. */
    runId: string;
    totalCategories: number;
    completedCategories: number;
    inProgress: boolean;
  };
  
  // UI state
  loading: LoadingState;
  selectedScenarios: number[];

  // Alternate scenario tracking (persists across page navigation within session)
  pinnedAlternateScenarioIds: number[];
  // Maps alternate scenario ID → parent scenario ID it was derived from
  alternateParentMap: Record<number, number>;

  // Excluded payer volume info per category (from greedy generation)
  excludedPayerInfo: Record<string, { excludedPayers: string[]; excludedPayerVolume: number }>;

  // PF IDs missing a fee schedule, per category (weighted NCR falls back to Medicare for these)
  missingFeeSchedulePfIds: Record<string, string[]>;

  // Per-PF partial fee-schedule gaps, per category: pfId -> gap. The PF has a schedule but is
  // missing rates for some eligible payers, so their volume uses Medicare (weighted NCR is
  // approximate). Distinct from missingFeeSchedulePfIds, which is whole-PF-missing.
  feeScheduleGaps: Record<string, Record<string, FeeScheduleGap>>;

  // Per-payer profitability (fee-schedule reimbursement based), keyed by scenarioKey
  // (category + scenario_id: generated ids restart at 1 in every area).
  // Persisted like results/payer assignments — a snapshot that only refreshes on a new
  // generation. Fee schedules change ~quarterly, so brief staleness is acceptable.
  payerProfitabilityByScenario: Record<string, Record<string, PayerProfitabilityItem>>;

  // Contract-opportunity + tier rebate breakdown rows from the generation response.
  // Session-only (NOT persisted to localStorage — can be thousands of rows); after a
  // page refresh the drill-down prompts the user to regenerate.
  rebateDetails: RebateDetail[];

  token: string;

  currentOrg: string | null;
}

// Action types
type ScenarioAction =
  | { type: 'SET_LOADING'; payload: { isLoading: boolean; error?: string } }
  | { type: 'SET_CURRENT_USER'; payload: string }
  | { type: 'SET_CURRENT_CATEGORY'; payload: string }
  | { type: 'SET_USER_PRODUCT_FAMILIES'; payload: UserProductFamilies }
  | { type: 'SET_SCENARIOS'; payload: Scenario[] }
  | { type: 'SET_SCENARIO_RESULTS'; payload: { results: ScenarioResult[]; summary: ScenarioSummary[] } }
  | { type: 'SET_GENERATOR_STATE'; payload: Partial<ScenarioState['generatorState']> }
  | { type: 'MERGE_GENERATOR_FORM'; payload: Partial<ScenarioFormState> }
  | { type: 'MERGE_GENERATOR_FAMILIES'; payload: Record<string, Partial<ProductFamilySelection>> }
  | { type: 'ADD_SCENARIO'; payload: Scenario }
  | { type: 'REMOVE_SCENARIO'; payload: number }
  | { type: 'CLEAR_SCENARIOS' }
  | { type: 'SET_SELECTED_SCENARIOS'; payload: number[] }
  | { type: 'TOGGLE_SCENARIO_SELECTION'; payload: number }
  | { type: 'CLEAR_ALL' }
  | { type: 'SET_CURRENT_ORG'; payload: string }
  | { type: 'APPEND_SCENARIO_RESULTS'; payload: { results: ScenarioResult[]; summary: ScenarioSummary[] } }
  | { type: 'INIT_GENERATION_SESSION'; payload: { runId: string; totalCategories: number } }
  | { type: 'RUN_SCOPED'; payload: { runId: string; action: ScenarioAction } }
  | { type: 'SWITCH_PRACTICE'; payload: { account: string; label: string } }
  | { type: 'INCREMENT_GENERATION_COMPLETED' }
  | { type: 'END_GENERATION_SESSION' }
  | { type: 'SET_PINNED_ALTERNATE_SCENARIO_IDS'; payload: number[] }
  | { type: 'MERGE_ALTERNATE_PARENT_MAP'; payload: Record<number, number> }
  | { type: 'SET_EXCLUDED_PAYER_INFO'; payload: { category: string; excludedPayers: string[]; excludedPayerVolume: number } }
  | { type: 'SET_MISSING_FEE_SCHEDULE_PF_IDS'; payload: { category: string; pfIds: string[] } }
  | { type: 'SET_FEE_SCHEDULE_GAPS'; payload: { category: string; gaps: Record<string, FeeScheduleGap> } }
  | { type: 'MERGE_PAYER_PROFITABILITY'; payload: { category: string; byScenarioId: Record<string, Record<string, PayerProfitabilityItem>> } }
  | { type: 'MERGE_REBATE_DETAILS'; payload: RebateDetail[] };

// Initial state
const initialState: ScenarioState = {
  currentUser: null,
  currentCategory: null,
  userProductFamilies: null,
  generatorState: {
    form: {},
    selectedFamilies: {},
    userName: null,
    useCurrentOnly: false,
  },
  scenarios: [],
  scenarioResults: [],
  scenarioSummary: [],
  loading: { isLoading: false },
  selectedScenarios: [],
  pinnedAlternateScenarioIds: [],
  alternateParentMap: {},
  excludedPayerInfo: {},
  missingFeeSchedulePfIds: {},
  feeScheduleGaps: {},
  payerProfitabilityByScenario: {},
  rebateDetails: [],
  token: '',
  currentOrg: null,
  generationStatus: undefined,
  selectedExcludedOpportunities: null,
};

// Reducer function
const scenarioReducer = (state: ScenarioState, action: ScenarioAction): ScenarioState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        loading: action.payload,
      };

    case 'SET_GENERATOR_STATE':
      return {
        ...state,
        generatorState: {
          ...state.generatorState,
          ...action.payload,
        },
      };

    // Patches merged into the *current* form / selections, so two updates dispatched in the
    // same tick don't overwrite each other with a stale copy captured at render time.
    case 'MERGE_GENERATOR_FORM':
      return {
        ...state,
        generatorState: {
          ...state.generatorState,
          form: { ...state.generatorState.form, ...action.payload },
        },
      };

    case 'MERGE_GENERATOR_FAMILIES': {
      const families = { ...state.generatorState.selectedFamilies };
      Object.entries(action.payload).forEach(([name, patch]) => {
        families[name] = { ...(families[name] ?? { include: false, minDistribution: 0 }), ...patch } as ProductFamilySelection;
      });
      return {
        ...state,
        generatorState: { ...state.generatorState, selectedFamilies: families },
      };
    }

    case 'SET_CURRENT_USER':
      return {
        ...state,
        currentUser: action.payload,
      };

    case 'SET_CURRENT_ORG':
      return {
        ...state,
        currentOrg: action.payload,
      };

    case 'SET_CURRENT_CATEGORY':
      return {
        ...state,
        currentCategory: action.payload,
      };

    case 'SET_USER_PRODUCT_FAMILIES':
      return {
        ...state,
        userProductFamilies: action.payload,
      };

    case 'SET_SCENARIOS':
      return {
        ...state,
        scenarios: action.payload,
        selectedScenarios: [], // Clear selection when new scenarios are loaded
      };

    case 'SET_SCENARIO_RESULTS':
      return {
        ...state,
        scenarioResults: action.payload.results,
        scenarioSummary: action.payload.summary,
        pinnedAlternateScenarioIds: [],
        alternateParentMap: {},
        excludedPayerInfo: {},
        missingFeeSchedulePfIds: {},
        feeScheduleGaps: {},
        // New generation invalidates the profitability snapshot.
        payerProfitabilityByScenario: {},
        // New generation invalidates the rebate drill-down snapshot.
        rebateDetails: [],
      };

    case 'APPEND_SCENARIO_RESULTS': {
      // Merge results avoiding duplicates (by scenario_id + product_family_id)
      const existingMap = new Map<string, ScenarioResult>();
      state.scenarioResults.forEach(r => existingMap.set(`${r.scenario_id}:${r.product_family_id}`, r));
      action.payload.results.forEach(r => {
        existingMap.set(`${r.scenario_id}:${r.product_family_id}`, r);
      });
      const mergedResults = Array.from(existingMap.values());
      // Merge summaries (replace by scenario_id + category to preserve per-category payer_assignments)
      const summaryMap = new Map<string, ScenarioSummary>();
      state.scenarioSummary.forEach(s => summaryMap.set(`${s.scenario_id}:${s.category ?? ''}`, s));
      action.payload.summary.forEach(s => summaryMap.set(`${s.scenario_id}:${s.category ?? ''}`, s));
      const mergedSummary = Array.from(summaryMap.values());
      return {
        ...state,
        scenarioResults: mergedResults,
        scenarioSummary: mergedSummary,
      };
    }

    case 'INIT_GENERATION_SESSION':
      return {
        ...state,
        generationStatus: {
          runId: action.payload.runId,
          totalCategories: action.payload.totalCategories,
          completedCategories: 0,
          inProgress: true,
        },
      };

    case 'INCREMENT_GENERATION_COMPLETED':
      return {
        ...state,
        generationStatus: state.generationStatus
          ? {
              ...state.generationStatus,
              completedCategories: Math.min(
                state.generationStatus.completedCategories + 1,
                state.generationStatus.totalCategories
              ),
            }
          : state.generationStatus,
      };

    case 'END_GENERATION_SESSION':
      return {
        ...state,
        generationStatus: state.generationStatus
          ? { ...state.generationStatus, inProgress: false }
          : state.generationStatus,
      };

    case 'ADD_SCENARIO':
      return {
        ...state,
        scenarios: [...state.scenarios, action.payload],
      };

    case 'REMOVE_SCENARIO':
      return {
        ...state,
        scenarios: state.scenarios.filter(s => s.scenario_id !== action.payload),
        selectedScenarios: state.selectedScenarios.filter(id => id !== action.payload),
      };

    case 'CLEAR_SCENARIOS':
      return {
        ...state,
        scenarios: [],
        scenarioResults: [],
        scenarioSummary: [],
        selectedScenarios: [],
        payerProfitabilityByScenario: {},
        rebateDetails: [],
        // Also clear generator state when explicitly clearing scenarios
        generatorState: {
          form: {},
          selectedFamilies: {},
          userName: null,
          useCurrentOnly: false,
        },
      };

    case 'SET_SELECTED_SCENARIOS':
      return {
        ...state,
        selectedScenarios: action.payload,
      };

    case 'TOGGLE_SCENARIO_SELECTION':
      const scenarioId = action.payload;
      const isSelected = state.selectedScenarios.includes(scenarioId);
      return {
        ...state,
        selectedScenarios: isSelected
          ? state.selectedScenarios.filter(id => id !== scenarioId)
          : [...state.selectedScenarios, scenarioId],
      };

    case 'SET_PINNED_ALTERNATE_SCENARIO_IDS':
      return {
        ...state,
        pinnedAlternateScenarioIds: action.payload,
      };

    case 'MERGE_ALTERNATE_PARENT_MAP':
      return {
        ...state,
        alternateParentMap: { ...state.alternateParentMap, ...action.payload },
      };

    case 'SET_EXCLUDED_PAYER_INFO':
      return {
        ...state,
        excludedPayerInfo: {
          ...state.excludedPayerInfo,
          [action.payload.category]: {
            excludedPayers: action.payload.excludedPayers,
            excludedPayerVolume: action.payload.excludedPayerVolume,
          },
        },
      };

    case 'SET_MISSING_FEE_SCHEDULE_PF_IDS':
      return {
        ...state,
        missingFeeSchedulePfIds: {
          ...state.missingFeeSchedulePfIds,
          [action.payload.category]: action.payload.pfIds,
        },
      };

    case 'SET_FEE_SCHEDULE_GAPS':
      return {
        ...state,
        feeScheduleGaps: {
          ...state.feeScheduleGaps,
          [action.payload.category]: action.payload.gaps,
        },
      };

    case 'MERGE_PAYER_PROFITABILITY':
      return {
        ...state,
        payerProfitabilityByScenario: {
          ...state.payerProfitabilityByScenario,
          ...Object.fromEntries(Object.entries(action.payload.byScenarioId).map(
            ([sid, byPayer]) => [scenarioKey(action.payload.category, sid), byPayer]
          )),
        },
      };

    case 'MERGE_REBATE_DETAILS': {
      // Replace-by-key so re-running a category refreshes its rows instead of appending.
      // Relies on contract_opportunities_id being stable across runs, which is why the
      // backend strips the SP's placeholder ids.
      const detailKey = (d: RebateDetail) =>
        `${d.scenario_id}:${d.product_family_id}:${d.rebate_source}:${d.contract_opportunities_id ?? ''}:${d.tier ?? ''}:${d.paid_on ?? ''}:${d.paid_as_value ?? ''}`;
      const merged = new Map<string, RebateDetail>();
      (state.rebateDetails ?? []).forEach(d => merged.set(detailKey(d), d));
      action.payload.forEach(d => merged.set(detailKey(d), d));
      return {
        ...state,
        rebateDetails: Array.from(merged.values()),
      };
    }

    // Applies a generation run's update only while that run is the current one: after a
    // practice switch or a newer run, the old run's background areas must not land here.
    case 'RUN_SCOPED':
      return state.generationStatus?.runId === action.payload.runId
        ? scenarioReducer(state, action.payload.action)
        : state;

    // A new practice starts clean: a fresh form, and nothing from the previous practice's run
    // (results, pins, profitability, rebate rows, banners, or a run still in progress).
    case 'SWITCH_PRACTICE':
      return {
        ...state,
        currentUser: action.payload.account,
        currentCategory: null,
        generatorState: {
          form: { logged_in_user: action.payload.account, categories: [], max_scenarios: 25 },
          selectedFamilies: {},
          userName: action.payload.label,
          useCurrentOnly: false,
          preferredGenerationMode: state.generatorState.preferredGenerationMode,
        },
        scenarios: [],
        scenarioResults: [],
        scenarioSummary: [],
        selectedScenarios: [],
        pinnedAlternateScenarioIds: [],
        alternateParentMap: {},
        excludedPayerInfo: {},
        missingFeeSchedulePfIds: {},
        feeScheduleGaps: {},
        payerProfitabilityByScenario: {},
        rebateDetails: [],
        generationStatus: undefined,
      };

    case 'CLEAR_ALL':
      return initialState;

    default:
      return state;
  }
};

// Context
const ScenarioContext = createContext<{
  state: ScenarioState;
  dispatch: React.Dispatch<ScenarioAction>;
} | null>(null);

// Provider component
interface ScenarioProviderProps {
  children: ReactNode;
}

const LOCAL_STORAGE_KEY = 'scenarioState';
// Rebate rows get their own key so the frequent small writes (every generator keystroke) don't
// re-serialize megabytes of rows that only change on a generation.
const REBATE_STORAGE_KEY = 'scenarioState.rebateDetails';
// Results-page star cache. Owned by ScenarioResults/FavoriteScenarios, cleared with the rest.
export const FAVORITES_UI_CACHE_KEY = 'scenarioiq_favorites_ui_cache';
const WAS_GENERATING_SESSION_KEY = 'was_generating';

// Bump when a persisted slice changes shape; saved data from another version is discarded
// instead of being shallow-merged into the new shape.
const PERSIST_VERSION = 2;
const PERSIST_DEBOUNCE_MS = 400;

// The only slices written to storage. Transient fields (loading, generationStatus, token,
// selections) are deliberately absent: restoring them after a reload is what left the
// "Generating categories" banner stuck.
const PERSISTED_KEYS = [
  'currentUser',
  'currentCategory',
  'userProductFamilies',
  'currentOrg',
  'generatorState',
  'scenarios',
  'scenarioResults',
  'scenarioSummary',
  'pinnedAlternateScenarioIds',
  'alternateParentMap',
  'excludedPayerInfo',
  'missingFeeSchedulePfIds',
  'feeScheduleGaps',
  'payerProfitabilityByScenario',
] as const;
type PersistedSlices = Pick<ScenarioState, typeof PERSISTED_KEYS[number]>;

// Every saved blob records the MSAL account that wrote it. Data belonging to anyone else is
// never loaded, so a second user on a shared machine starts clean.
interface PersistedEnvelope<T> {
  version: number;
  accountId: string;
  data: T;
}

// Set at logout so the state reset that follows (and any pending debounced write) can't put
// the data back before the logout redirect leaves the page.
let persistenceSuspended = false;

const clearPersistedStorage = () => {
  try {
    localStorage.removeItem(LOCAL_STORAGE_KEY);
    localStorage.removeItem(REBATE_STORAGE_KEY);
    localStorage.removeItem(FAVORITES_UI_CACHE_KEY);
    sessionStorage.removeItem(WAS_GENERATING_SESSION_KEY);
  } catch {
    // Storage unavailable: nothing was persisted either.
  }
};

/** Wipes persisted scenario state and stops further writes for the rest of this page load. */
export const discardPersistedState = () => {
  persistenceSuspended = true;
  clearPersistedStorage();
};

// Byte budget for the rebate rows in localStorage. Size, not row count, is what risks a
// QuotaExceededError. Trimmed rows measure ~500 bytes, so this holds ~5,000 — enough for 25
// scenarios against a 30-tier contract — and still leaves about half a 5 M-char origin quota
// for everything else. Over budget the rows are dropped and the drill-down asks for a rerun.
const MAX_PERSISTED_REBATE_BYTES = 2_500_000;

// Listed explicitly so a field added to the model later cannot silently bloat storage.
const trimRebateDetailForStorage = (d: RebateDetail): RebateDetail => ({
  scenario_id: d.scenario_id,
  logged_in_user: d.logged_in_user,
  product_family_id: d.product_family_id,
  product_family_name: d.product_family_name,
  rebate_source: d.rebate_source,
  contract_opportunities_id: d.contract_opportunities_id ?? null,
  has_contract: d.has_contract,
  opportunity: d.opportunity ?? null,
  paid_on: d.paid_on ?? null,
  trigger_type: d.trigger_type ?? null,
  unit_of_measure: d.unit_of_measure ?? null,
  tier: d.tier ?? null,
  min_calc: d.min_calc ?? null,
  secondary_min_calc: d.secondary_min_calc ?? null,
  total_units: d.total_units ?? null,
  secondary_total_units: d.secondary_total_units ?? null,
  paid_as_value: d.paid_as_value ?? null,
  rebate_earned: d.rebate_earned ?? null,
  exclusion_reason: d.exclusion_reason ?? null,
});

const readEnvelope = <T,>(key: string, accountId: string): T | null => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const envelope = JSON.parse(raw) as Partial<PersistedEnvelope<T>>;
    return envelope?.version === PERSIST_VERSION && envelope.accountId === accountId
      ? (envelope.data ?? null)
      : null;
  } catch {
    console.warn('Failed to read saved scenario state');
    return null;
  }
};

const loadPersistedState = (accountId: string | null): ScenarioState => {
  // Not signed in yet (e.g. mid login redirect): start empty and leave storage alone. The
  // provider remounts with the account once MSAL has it.
  if (!accountId) return initialState;
  const saved = readEnvelope<Partial<PersistedSlices>>(LOCAL_STORAGE_KEY, accountId);
  if (!saved) {
    // Whatever is stored belongs to another account or an older build: drop it.
    clearPersistedStorage();
    return initialState;
  }
  const restored: Partial<ScenarioState> = {};
  PERSISTED_KEYS.forEach((key) => {
    if (saved[key] !== undefined) (restored as Record<string, unknown>)[key] = saved[key];
  });
  const rebateDetails = readEnvelope<RebateDetail[]>(REBATE_STORAGE_KEY, accountId) ?? [];
  return { ...initialState, ...restored, rebateDetails };
};

const saveSlices = (accountId: string, state: ScenarioState) => {
  if (persistenceSuspended) return;
  const data: Record<string, unknown> = {};
  PERSISTED_KEYS.forEach((key) => { data[key] = state[key]; });
  const envelope: PersistedEnvelope<Record<string, unknown>> = { version: PERSIST_VERSION, accountId, data };
  const serialized = JSON.stringify(envelope);
  try {
    localStorage.setItem(LOCAL_STORAGE_KEY, serialized);
  } catch {
    // Most likely the quota. Give up the rebate rows (the drill-down then asks for a rerun) so
    // results and generator state still survive.
    try {
      localStorage.removeItem(REBATE_STORAGE_KEY);
      localStorage.setItem(LOCAL_STORAGE_KEY, serialized);
    } catch {
      console.warn('Failed to save scenario state to localStorage');
    }
  }
};

const saveRebateDetails = (accountId: string, rebateDetails: RebateDetail[]) => {
  if (persistenceSuspended) return;
  try {
    const rows = JSON.stringify(rebateDetails.map(trimRebateDetailForStorage));
    if (rebateDetails.length === 0 || rows.length > MAX_PERSISTED_REBATE_BYTES) {
      localStorage.removeItem(REBATE_STORAGE_KEY);
      return;
    }
    // Spliced by hand so the (up to 2.5 MB) rows are serialized once, not again inside an envelope.
    localStorage.setItem(
      REBATE_STORAGE_KEY,
      `{"version":${PERSIST_VERSION},"accountId":${JSON.stringify(accountId)},"data":${rows}}`
    );
  } catch {
    try { localStorage.removeItem(REBATE_STORAGE_KEY); } catch { /* storage unavailable */ }
  }
};

// The run currently in progress, readable from a generation loop whose component may have
// unmounted (the remaining areas keep generating after the page moves to Results).
let activeRunId: string | undefined;
/** False once the run was superseded (practice switch, newer run, logout): stop its loop. */
export const isGenerationRunActive = (runId: string): boolean => activeRunId === runId;

const AccountScopedProvider: React.FC<ScenarioProviderProps & { accountId: string | null }> = ({ accountId, children }) => {
  const [state, dispatch] = React.useReducer(scenarioReducer, accountId, loadPersistedState);
  activeRunId = state.generationStatus?.inProgress ? state.generationStatus.runId : undefined;

  const latestState = React.useRef(state);
  latestState.current = state;
  const savePending = React.useRef(false);

  const flushSave = React.useCallback(() => {
    if (!accountId || !savePending.current) return;
    savePending.current = false;
    saveSlices(accountId, latestState.current);
  }, [accountId]);

  // Debounced: the generator form dispatches on every keystroke.
  React.useEffect(() => {
    if (!accountId) return;
    savePending.current = true;
    const timer = window.setTimeout(flushSave, PERSIST_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  // Only the persisted slices: anything else changing has nothing new to save.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    flushSave,
    state.currentUser,
    state.currentCategory,
    state.userProductFamilies,
    state.currentOrg,
    state.generatorState,
    state.scenarios,
    state.scenarioResults,
    state.scenarioSummary,
    state.pinnedAlternateScenarioIds,
    state.alternateParentMap,
    state.excludedPayerInfo,
    state.missingFeeSchedulePfIds,
    state.feeScheduleGaps,
    state.payerProfitabilityByScenario,
  ]);

  // A reload or tab close inside the debounce window would otherwise lose the last change.
  React.useEffect(() => {
    window.addEventListener('pagehide', flushSave);
    return () => window.removeEventListener('pagehide', flushSave);
  }, [flushSave]);

  // Rebate rows only change on a generation, so they're written on their own, undebounced.
  React.useEffect(() => {
    if (accountId) saveRebateDetails(accountId, state.rebateDetails ?? []);
  }, [accountId, state.rebateDetails]);

  return (
    <ScenarioContext.Provider value={{ state, dispatch }}>
      {children}
    </ScenarioContext.Provider>
  );
};

export const ScenarioProvider: React.FC<ScenarioProviderProps> = ({ children }) => {
  const { instance, accounts } = useMsal();
  const accountId = (instance.getActiveAccount() ?? accounts[0])?.homeAccountId ?? null;
  // Keyed by account: a different sign-in remounts with that account's own saved state.
  return (
    <AccountScopedProvider key={accountId ?? 'signed-out'} accountId={accountId}>
      {children}
    </AccountScopedProvider>
  );
};


// Custom hook to use the context
export const useScenarioContext = () => {
  const context = useContext(ScenarioContext);
  if (!context) {
    throw new Error('useScenarioContext must be used within a ScenarioProvider');
  }
  return context;
};

// Action creators (helper functions)
export const scenarioActions = {
  setLoading: (isLoading: boolean, error?: string) => ({
    type: 'SET_LOADING' as const,
    payload: { isLoading, error },
  }),

  setCurrentUser: (userId: string) => ({
    type: 'SET_CURRENT_USER' as const,
    payload: userId,
  }),

  setCurrentOrg: (orgName: string) => ({
    type: 'SET_CURRENT_ORG' as const,
    payload: orgName,
  }),

  setGeneratorState: (newState: Partial<ScenarioState['generatorState']>) => ({
    type: 'SET_GENERATOR_STATE' as const,
    payload: newState,
  }),

  mergeGeneratorForm: (patch: Partial<ScenarioFormState>) => ({
    type: 'MERGE_GENERATOR_FORM' as const,
    payload: patch,
  }),

  mergeGeneratorFamilies: (patch: Record<string, Partial<ProductFamilySelection>>) => ({
    type: 'MERGE_GENERATOR_FAMILIES' as const,
    payload: patch,
  }),

  setCurrentCategory: (category: string) => ({
    type: 'SET_CURRENT_CATEGORY' as const,
    payload: category,
  }),

  setUserProductFamilies: (userProductFamilies: UserProductFamilies) => ({
    type: 'SET_USER_PRODUCT_FAMILIES' as const,
    payload: userProductFamilies,
  }),

  setScenarios: (scenarios: Scenario[]) => ({
    type: 'SET_SCENARIOS' as const,
    payload: scenarios,
  }),

  setScenarioResults: (results: ScenarioResult[], summary: ScenarioSummary[]) => ({
    type: 'SET_SCENARIO_RESULTS' as const,
    payload: { results, summary },
  }),

  appendScenarioResults: (results: ScenarioResult[], summary: ScenarioSummary[]) => ({
    type: 'APPEND_SCENARIO_RESULTS' as const,
    payload: { results, summary },
  }),

  addScenario: (scenario: Scenario) => ({
    type: 'ADD_SCENARIO' as const,
    payload: scenario,
  }),

  removeScenario: (scenarioId: number) => ({
    type: 'REMOVE_SCENARIO' as const,
    payload: scenarioId,
  }),

  clearScenarios: () => ({
    type: 'CLEAR_SCENARIOS' as const,
  }),

  setSelectedScenarios: (scenarioIds: number[]) => ({
    type: 'SET_SELECTED_SCENARIOS' as const,
    payload: scenarioIds,
  }),

  toggleScenarioSelection: (scenarioId: number) => ({
    type: 'TOGGLE_SCENARIO_SELECTION' as const,
    payload: scenarioId,
  }),

  clearAll: () => ({
    type: 'CLEAR_ALL' as const,
  }),

  initGenerationSession: (runId: string, totalCategories: number) => ({
    type: 'INIT_GENERATION_SESSION' as const,
    payload: { runId, totalCategories },
  }),

  /** Wraps an update from a generation run so it is dropped once that run is no longer current. */
  forRun: (runId: string, action: ScenarioAction) => ({
    type: 'RUN_SCOPED' as const,
    payload: { runId, action },
  }),

  switchPractice: (account: string, label: string) => ({
    type: 'SWITCH_PRACTICE' as const,
    payload: { account, label },
  }),

  incrementGenerationCompleted: () => ({
    type: 'INCREMENT_GENERATION_COMPLETED' as const,
  }),

  endGenerationSession: () => ({
    type: 'END_GENERATION_SESSION' as const,
  }),

  setPinnedAlternateScenarioIds: (ids: number[]) => ({
    type: 'SET_PINNED_ALTERNATE_SCENARIO_IDS' as const,
    payload: ids,
  }),

  mergeAlternateParentMap: (map: Record<number, number>) => ({
    type: 'MERGE_ALTERNATE_PARENT_MAP' as const,
    payload: map,
  }),

  setExcludedPayerInfo: (category: string, excludedPayers: string[], excludedPayerVolume: number) => ({
    type: 'SET_EXCLUDED_PAYER_INFO' as const,
    payload: { category, excludedPayers, excludedPayerVolume },
  }),

  setMissingFeeSchedulePfIds: (category: string, pfIds: string[]) => ({
    type: 'SET_MISSING_FEE_SCHEDULE_PF_IDS' as const,
    payload: { category, pfIds },
  }),

  setFeeScheduleGaps: (category: string, gaps: Record<string, FeeScheduleGap>) => ({
    type: 'SET_FEE_SCHEDULE_GAPS' as const,
    payload: { category, gaps },
  }),

  // byScenarioId is the generation response's `profitability` (scenario ids as keys) for one area.
  mergePayerProfitability: (category: string, byScenarioId: Record<string, Record<string, PayerProfitabilityItem>>) => ({
    type: 'MERGE_PAYER_PROFITABILITY' as const,
    payload: { category, byScenarioId },
  }),

  mergeRebateDetails: (details: RebateDetail[]) => ({
    type: 'MERGE_REBATE_DETAILS' as const,
    payload: details,
  }),
};
